import React, { useState, useRef } from 'react';
import api from '../lib/api';
import { Play, Pause, Loader2, Download } from 'lucide-react';

/**
 * Plays a Yeastar call recording. The stream endpoint needs an auth header,
 * so we fetch the audio as a blob (with the axios interceptor's token) and play
 * it via an object URL rather than a plain <audio src>.
 */
export const CallRecordingPlayer = ({ callLogId }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/yeastar/recording/${callLogId}`, { responseType: 'blob' });
      const objUrl = URL.createObjectURL(res.data);
      setUrl(objUrl);
      setTimeout(() => { audioRef.current?.play(); }, 50);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!url) { load(); return; }
    if (playing) audioRef.current?.pause();
    else audioRef.current?.play();
  };

  return (
    <div className="flex items-center gap-2" data-testid={`recording-player-${callLogId}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={loading}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1 hover:bg-green-100 transition-colors"
        data-testid={`play-recording-${callLogId}`}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        {error ? 'Recording unavailable' : loading ? 'Loading…' : playing ? 'Pause' : 'Play recording'}
      </button>
      {url && (
        <a href={url} download={`call-${callLogId}.mp3`} className="text-slate-400 hover:text-slate-600" title="Download">
          <Download className="w-3.5 h-3.5" />
        </a>
      )}
      {url && (
        <audio
          ref={audioRef}
          src={url}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="hidden"
        />
      )}
    </div>
  );
};

export default CallRecordingPlayer;
